import { FC, useMemo, useState, useEffect, ReactElement } from 'react'
import { FormApi } from 'final-form'
import { Accordion, Card } from 'react-bootstrap'

import {
  InfoCard,
  DocumentListItem,
  ISafetyCaseFieldsAdditionalOptions,
  ISafetyCaseValues,
  ISafetyCaseUpsertValues,
} from '../../../components'
import SafetyCaseForm from './safety-case-form'

import { useSafetyReportActiveQuestions } from '../../../services'
import { renderLocalDate } from '../../../util'
import useFetchDocumentContent from '../../../components/form/document-upload/use-fetch-document-content'
import useDownloadDocument from '../../../components/form/document-upload/use-download-document'

import { ISafetyReport, ISafetyReportAnswer } from '../../../interfaces'

import s from './safety-case-accordion.module.scss'

type SafetyCaseInfoProps = {
  hasWriteAccess?: boolean
  setHeaderControls: (headerControls?: ReactElement) => void
  additionalSaveSafetyCaseOptions: ISafetyCaseFieldsAdditionalOptions
  handleSaveSafetyCase: (safetyCaseValues: ISafetyCaseValues) => void
  handleUpdateSafetyReportInternalAnswer?: (payload: { id: number; internalAnswer: string }) => void
  saveHeaderControls: ReactElement
  setSaveSafetyCaseForm?: (saveSafetyCaseForm: FormApi<ISafetyCaseValues>) => void
  saveSafetyCaseInitialValues?: ISafetyCaseUpsertValues
  setSaveSafetyCaseValues?: (safetyCaseUpsertValues: ISafetyCaseUpsertValues) => void
  isSaveFormDisabled?: boolean
  safetyReport?: ISafetyReport
}

const SafetyCaseInfo: FC<SafetyCaseInfoProps> = props => {
  const {
    hasWriteAccess,
    setHeaderControls,
    additionalSaveSafetyCaseOptions,
    handleSaveSafetyCase,
    handleUpdateSafetyReportInternalAnswer,
    saveHeaderControls,
    setSaveSafetyCaseForm,
    saveSafetyCaseInitialValues,
    setSaveSafetyCaseValues,
    isSaveFormDisabled,
    safetyReport,
  } = props

  useEffect(() => {
    setHeaderControls(saveHeaderControls)
    return () => setHeaderControls(undefined)
  }, [setHeaderControls, saveHeaderControls])

  const { data: safetyReportQuestions } = useSafetyReportActiveQuestions(undefined, undefined)

  function getEditHistory(answer: ISafetyReportAnswer, safetyReport: ISafetyReport) {
    if (answer?.internalAnswer && answer?.internalAnswer !== answer?.externalAnswer) {
      return {
        original: {
          label: 'Original (External)',
          value: answer?.externalAnswer,
          dateTime: safetyReport.observedAt,
        },
        modified: {
          label: 'Modified (Internal)',
          value: answer?.internalAnswer,
          modifiedBy: answer?.modifiedByName,
          modifiedAt: answer?.modifiedDate,
        },
      }
    }
    return null
  }

  const infoCardOptions = useMemo(() => {
    if (!safetyReport) {
      return []
    }

    const answers = safetyReport.answers
      ? Object.values(safetyReport.answers)
          .filter(answer => !!answer?.externalAnswer)
          .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
          .map(answer => ({
            title: answer?.questionText,
            value: answer?.internalAnswer ?? answer?.externalAnswer,
            entityId: answer?.id,
            options: safetyReportQuestions?.find(q => q.id === answer.questionId)?.answerOptions,
            editHistory: getEditHistory(answer, safetyReport),
            showModifiedIndicator: !!(answer?.internalAnswer && answer?.internalAnswer !== answer?.externalAnswer),
            onChange: (entityId: number, selectedValue: string) => {
              handleUpdateSafetyReportInternalAnswer?.({ id: entityId, internalAnswer: selectedValue })
            },
          }))
      : []

    return [
      { title: 'Reporter Name', value: safetyReport.reporterName || 'Anonymous' },
      { title: 'Reporter Email Address', value: safetyReport.reporterEmailAddress, hiddenOnNoValue: true },
      { title: 'Reporter Phone', value: safetyReport.reporterPhoneNumber, hiddenOnNoValue: true },
      { title: 'Observed At', value: renderLocalDate(safetyReport.observedAt) },
      { title: 'Description', value: safetyReport.description },
      ...answers,
    ]
  }, [safetyReport, safetyReportQuestions, handleUpdateSafetyReportInternalAnswer])

  const fetchDocumentContent = useFetchDocumentContent()
  const { downloadDocument: handleDownloadDocument, downloadingDocumentIds } = useDownloadDocument(fetchDocumentContent)

  const [isReportedConcernOpen, setIsReportedConcernOpen] = useState<boolean>(true)

  return (
    <>
      <SafetyCaseForm
        onSubmit={handleSaveSafetyCase}
        disabled={isSaveFormDisabled || !hasWriteAccess}
        isViewMode={!hasWriteAccess}
        initialValues={saveSafetyCaseInitialValues}
        formName="safety-case-form"
        additionalOptions={additionalSaveSafetyCaseOptions}
        setForm={setSaveSafetyCaseForm}
        onChange={setSaveSafetyCaseValues}
        baseStatus={'Doing'}
      />

      {safetyReport && (
        <Accordion>
          <Card className={`d-flex flex-column mr-4 mb-8 ${s['card']} ${s['accordion-heading']}`}>
            <Accordion.Toggle
              as={Card.Header}
              eventKey="0"
              className={s['heading']}
              onClick={() => setIsReportedConcernOpen(open => !open)}
            >
              <h5 className="mb-0">Reported Concern</h5>
              <div>
                {isReportedConcernOpen ? (
                  <i className="bi bi-chevron-up ml-2" />
                ) : (
                  <i className="bi bi-chevron-down ml-2" />
                )}
              </div>
            </Accordion.Toggle>

            <Accordion.Collapse eventKey="0" in={isReportedConcernOpen}>
              <Card.Body>
                <InfoCard options={infoCardOptions} showContainer={false}>
                  {safetyReport.documents && safetyReport.documents?.length > 0 && (
                    <div className="mt-4">
                      <h5>Attachments</h5>

                      {safetyReport.documents.map(document => (
                        <DocumentListItem
                          key={document.id}
                          document={document}
                          downloadDocument={() => handleDownloadDocument(document)}
                          isDocumentDownloading={downloadingDocumentIds.includes(document.id)}
                        />
                      ))}
                    </div>
                  )}
                </InfoCard>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      )}
    </>
  )
}

export default SafetyCaseInfo
